import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { downloadCsv } from "@/lib/csv";
import { useAdmin } from "@/lib/adminContext";
import {
  Users, UserPlus, ListPlus, KeyRound, Power, PowerOff, Search, Loader2,
  Download, CheckCircle2, Copy, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

const IT_DEPARTMENT = "IT / Information Technology";

function copyToClipboard(text) {
  try { navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
  catch { toast.error("Copy failed"); }
}

// One-time credential reveal dialog — the plaintext PIN is only shown here.
function CredentialsRevealDialog({ open, onClose, users }) {
  if (!open) return null;
  const list = users || [];
  const copyAll = () => {
    const lines = ["Full Name,Username,PIN", ...list.map((u) => `"${u.full_name}",${u.username},${u.pin}`)];
    copyToClipboard(lines.join("\n"));
  };
  const exportCsv = () => {
    downloadCsv(
      `iq3b9-credentials-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        { key: "full_name", header: "Full Name" },
        { key: "username", header: "Username" },
        { key: "pin", header: "PIN" },
      ],
      list,
    );
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="credentials-reveal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-600" />
            One-time credentials
          </DialogTitle>
          <DialogDescription>
            These PINs are shown only once. Copy or export them before closing this dialog.
          </DialogDescription>
        </DialogHeader>
        <div className="border rounded-lg overflow-hidden max-h-[45vh] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[color:var(--enka-blue-50)]/60">
                <TableHead>Full Name</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>PIN</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((u) => (
                <TableRow key={u.id} data-testid={`credential-row-${u.username}`}>
                  <TableCell>{u.full_name}</TableCell>
                  <TableCell className="font-mono text-sm">{u.username}</TableCell>
                  <TableCell className="font-mono font-bold text-[color:var(--enka-blue)]" data-testid={`credential-pin-${u.username}`}>
                    {u.pin}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => copyToClipboard(`${u.username} / ${u.pin}`)}
                      data-testid={`credential-copy-${u.username}`}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={copyAll} data-testid="credentials-copy-all-btn">
            <Copy className="h-4 w-4 mr-1.5" /> Copy all
          </Button>
          <Button
            onClick={exportCsv}
            className="bg-[color:var(--enka-blue)] hover:bg-[color:var(--enka-blue-600)] text-white"
            data-testid="credentials-export-csv-btn"
          >
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
          <Button variant="ghost" onClick={onClose} data-testid="credentials-close-btn">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Add single user
function AddUserDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("Site");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setName(""); setType("Site"); } }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/users", { full_name: name.trim(), user_type: type });
      onCreated([data]);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create user");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-user-dialog">
        <DialogHeader>
          <DialogTitle>Add user</DialogTitle>
          <DialogDescription>Username auto-generated from name. A 6-digit PIN is created once.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Full name</Label>
            <Input
              autoFocus value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Ali Kaya" required data-testid="add-user-name-input"
            />
          </div>
          <div>
            <Label>User type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="add-user-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Site">Site</SelectItem>
                <SelectItem value="Camp">Camp</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="submit" disabled={saving || !name.trim()}
              className="bg-[color:var(--enka-blue)] hover:bg-[color:var(--enka-blue-600)] text-white"
              data-testid="add-user-submit-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Bulk import users
function BulkImportDialog({ open, onClose, onCreated }) {
  const [text, setText] = useState("");
  const [type, setType] = useState("Site");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setText(""); setType("Site"); } }, [open]);
  const count = text.split("\n").map((l) => l.trim()).filter(Boolean).length;
  const submit = async () => {
    const names = text.split("\n").map((l) => l.trim()).filter(Boolean);
    if (names.length === 0) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/users/bulk", { names, user_type: type });
      onCreated(data.created || []);
      if (data.skipped?.length) {
        toast.warning(`${data.skipped.length} entr${data.skipped.length === 1 ? "y" : "ies"} skipped`);
      }
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk import failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk import users</DialogTitle>
          <DialogDescription>
            Paste one full name per line. Turkish characters are automatically normalized for usernames.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>User type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="bulk-user-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Site">Site</SelectItem>
                <SelectItem value="Camp">Camp</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Names ({count})</Label>
            <Textarea
              rows={10} value={text} onChange={(e) => setText(e.target.value)}
              placeholder={"İskender Özdemir\nUğur Balbay\nMehmet Bilal"}
              data-testid="bulk-names-textarea"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit} disabled={saving || count === 0}
            className="bg-[color:var(--enka-blue)] hover:bg-[color:var(--enka-blue-600)] text-white"
            data-testid="bulk-import-submit-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `Import ${count} user${count === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Edit / rename user
function EditUserDialog({ user, onClose, onSaved }) {
  const [name, setName] = useState(user?.full_name || "");
  const [type, setType] = useState(user?.user_type || "Site");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (user) { setName(user.full_name); setType(user.user_type || "Site"); }
  }, [user]);
  if (!user) return null;
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {};
      if (name.trim() !== user.full_name) body.full_name = name.trim();
      if (type !== user.user_type) body.user_type = type;
      if (Object.keys(body).length === 0) { onClose(); return; }
      const { data } = await api.patch(`/admin/users/${user.id}`, body);
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={Boolean(user)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription>Changing the name does not change the username.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Full name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="edit-user-name-input" />
          </div>
          <div>
            <Label>Username</Label>
            <Input value={user.username} disabled className="font-mono" />
          </div>
          <div>
            <Label>User type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="edit-user-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Site">Site</SelectItem>
                <SelectItem value="Camp">Camp</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="submit" disabled={saving}
              className="bg-[color:var(--enka-blue)] hover:bg-[color:var(--enka-blue-600)] text-white"
              data-testid="edit-user-submit-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsers() {
  const { admin } = useAdmin();
  const canManage = admin && (admin.role === "super" || (admin.scope || []).includes(IT_DEPARTMENT));

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reveal, setReveal] = useState(null); // [{...user, pin}] one-time

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error("Failed to load users");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (canManage) fetchUsers(); }, [canManage]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return users.filter((u) => {
      if (statusFilter !== "all" && u.status !== statusFilter) return false;
      if (typeFilter !== "all" && u.user_type !== typeFilter) return false;
      if (!s) return true;
      return (
        u.full_name.toLowerCase().includes(s) ||
        u.username.toLowerCase().includes(s)
      );
    });
  }, [users, search, statusFilter, typeFilter]);

  const resetPin = async (u) => {
    if (!window.confirm(`Reset PIN for ${u.full_name}?\n\nThe old PIN will stop working immediately.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-pin`);
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, updated_at: data.updated_at } : x)));
      setReveal([data]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset PIN");
    }
  };

  const toggleStatus = async (u) => {
    const next = u.status === "Active" ? "Passive" : "Active";
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, { status: next });
      setUsers((list) => list.map((x) => (x.id === u.id ? data : x)));
      toast.success(`User ${next === "Active" ? "activated" : "deactivated"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update user");
    }
  };

  const exportAllList = () => {
    downloadCsv(
      `iq3b9-users-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        { key: "full_name", header: "Full Name" },
        { key: "username", header: "Username" },
        { key: "user_type", header: "Type" },
        { key: "status", header: "Status" },
        { key: "created_at", header: "Created" },
      ],
      filtered,
    );
  };

  if (!canManage) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="admin-users-forbidden">
        <Card className="p-6 text-center">
          <ShieldAlert className="h-10 w-10 mx-auto text-amber-500" />
          <h1 className="font-display text-xl mt-3 text-[color:var(--enka-ink)]">Not authorized</h1>
          <p className="text-sm text-[color:var(--enka-muted)] mt-1">
            Only Super Admin or IT Admin can access this page.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="admin-users-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--enka-muted)] font-semibold">
            IQ3B9
          </div>
          <h1 className="font-display text-3xl text-[color:var(--enka-ink)] mt-1 flex items-center gap-2">
            <Users className="h-6 w-6 text-[color:var(--enka-blue)]" /> Users
          </h1>
          <p className="text-sm text-[color:var(--enka-muted)]">
            {users.length} personnel user{users.length === 1 ? "" : "s"}.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline" onClick={exportAllList}
            disabled={loading || filtered.length === 0}
            data-testid="users-export-list-btn"
            className="border-[color:var(--enka-blue)] text-[color:var(--enka-blue)] hover:bg-[color:var(--enka-blue-50)]"
          >
            <Download className="h-4 w-4 mr-1.5" /> Export list
          </Button>
          <Button
            variant="outline" onClick={() => setBulkOpen(true)}
            data-testid="users-bulk-btn"
          >
            <ListPlus className="h-4 w-4 mr-1.5" /> Bulk import
          </Button>
          <Button
            onClick={() => setAddOpen(true)}
            className="bg-[color:var(--enka-blue)] hover:bg-[color:var(--enka-blue-600)] text-white"
            data-testid="users-add-btn"
          >
            <UserPlus className="h-4 w-4 mr-1.5" /> Add user
          </Button>
        </div>
      </div>

      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--enka-muted)]" />
              <Input
                className="pl-8" placeholder="Name or username..."
                value={search} onChange={(e) => setSearch(e.target.value)}
                data-testid="users-search-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="users-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="Site">Site</SelectItem>
                <SelectItem value="Camp">Camp</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="users-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Passive">Passive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-[color:var(--enka-blue)]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-[color:var(--enka-muted)]" data-testid="users-empty">
            No users match the current filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[color:var(--enka-blue-50)]/60 hover:bg-[color:var(--enka-blue-50)]/60">
                  <TableHead className="font-semibold text-[color:var(--enka-ink)]">Full Name</TableHead>
                  <TableHead className="font-semibold text-[color:var(--enka-ink)]">Username</TableHead>
                  <TableHead className="font-semibold text-[color:var(--enka-ink)]">Type</TableHead>
                  <TableHead className="font-semibold text-[color:var(--enka-ink)]">Status</TableHead>
                  <TableHead className="text-right font-semibold text-[color:var(--enka-ink)]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.username}`}>
                    <TableCell className="font-medium">{u.full_name}</TableCell>
                    <TableCell className="font-mono text-sm">{u.username}</TableCell>
                    <TableCell>
                      <span className="enka-pill bg-slate-100 text-slate-800 border border-slate-200">
                        {u.user_type}
                      </span>
                    </TableCell>
                    <TableCell>
                      {u.status === "Active" ? (
                        <span className="enka-pill bg-emerald-50 text-emerald-800 border border-emerald-200">
                          <CheckCircle2 className="h-3 w-3" /> Active
                        </span>
                      ) : (
                        <span className="enka-pill bg-zinc-100 text-zinc-700 border border-zinc-200">
                          Passive
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button
                        variant="outline" size="sm"
                        onClick={() => setEditing(u)}
                        data-testid={`user-edit-btn-${u.username}`}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => resetPin(u)}
                        data-testid={`user-reset-pin-btn-${u.username}`}
                      >
                        <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset PIN
                      </Button>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => toggleStatus(u)}
                        data-testid={`user-toggle-status-btn-${u.username}`}
                      >
                        {u.status === "Active" ? (
                          <><PowerOff className="h-3.5 w-3.5 mr-1" /> Deactivate</>
                        ) : (
                          <><Power className="h-3.5 w-3.5 mr-1" /> Activate</>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <AddUserDialog
        open={addOpen} onClose={() => setAddOpen(false)}
        onCreated={(list) => { fetchUsers(); setReveal(list); }}
      />
      <BulkImportDialog
        open={bulkOpen} onClose={() => setBulkOpen(false)}
        onCreated={(list) => { fetchUsers(); if (list.length) setReveal(list); }}
      />
      <EditUserDialog
        user={editing} onClose={() => setEditing(null)}
        onSaved={(u) => setUsers((list) => list.map((x) => (x.id === u.id ? u : x)))}
      />
      <CredentialsRevealDialog
        open={Boolean(reveal)} onClose={() => setReveal(null)} users={reveal}
      />
    </div>
  );
}
